import type { PermissionTuple } from '~/utils/ability'
import type { LegacyRightsRequirement, PermissionRequirement } from '~/utils/rbac'
import { moduleIndexPermission } from '~/utils/rbacModuleIndex'

/**
 * Module 17 — Caméras. Deux fonctionnalités : le flux (écran « Caméras » et les vues caméras
 * personnalisées) et la configuration caméra d'une boutique (écran « Camera Config »).
 */

export const CAMERA_STREAM_READ: PermissionTuple = ['read', 'cameras.cameras.stream']
export const CAMERA_STREAM_ADD: PermissionTuple = ['add', 'cameras.cameras.stream']
export const CAMERA_STREAM_EDIT: PermissionTuple = ['edit', 'cameras.cameras.stream']
export const CAMERA_STREAM_DELETE: PermissionTuple = ['delete', 'cameras.cameras.stream']

export const CAMERA_CONFIG_READ: PermissionTuple = ['read', 'cameras.cameras.config']
export const CAMERA_CONFIG_ADD: PermissionTuple = ['add', 'cameras.cameras.config']
export const CAMERA_CONFIG_EDIT: PermissionTuple = ['edit', 'cameras.cameras.config']
export const CAMERA_CONFIG_DELETE: PermissionTuple = ['delete', 'cameras.cameras.config']

/**
 * Redémarrer le runtime caméra d'une boutique (coupe les flux le temps du redémarrage) et
 * synchroniser le référentiel Unifi : deux gestes d'exploitation, deux actions spéciales — au même
 * titre que `restart-kiosk` (audit du 19/09, TAX-M10).
 */
export const CAMERA_RUNTIME_RESTART: PermissionTuple = [
  'restart-camera-runtime',
  'cameras.cameras.config',
]
export const CAMERA_UNIFI_SYNC: PermissionTuple = ['sync-cameras', 'cameras.cameras.config']

/**
 * Ouvrir la porte d'une boutique à distance, et confirmer (ou refuser) une demande d'ouverture :
 * action spéciale du kiosk, distincte de la modification de sa fiche (audit du 19/09, TAX-M8).
 */
export const KIOSK_OPEN_DOOR: PermissionTuple = ['open-door', 'stores.kiosks.kiosk']

/**
 * Garde de l'index `/cameras` : l'entrée de menu du module est visible dès qu'un de ses écrans est
 * accessible, et `/cameras/config` n'est atteignable que depuis la barre d'outils de cet index — un
 * rôle porteur du seul `read cameras.cameras.config` doit donc pouvoir l'ouvrir (BLOQ-03). L'index
 * redirige alors vers la configuration.
 */
export const CAMERAS_PAGE_READ: PermissionRequirement = moduleIndexPermission([
  CAMERA_STREAM_READ,
  CAMERA_CONFIG_READ,
])

/**
 * Droits v1 exigés tant que le flag `permission-v2` est OFF (où tous les tuples v2 passent).
 * Les contrôleurs backend conservent leurs gardes historiques et ajoutent l'opérateur support
 * comme alternative pour l'ensemble de cette page.
 */
export const CAMERA_OPERATOR_LEGACY_RIGHTS: LegacyRightsRequirement = {
  any: ['ADMIN', 'SUPPORT_OPERATOR'],
}

export const CAMERA_STOCK_LEGACY_RIGHTS: LegacyRightsRequirement = {
  any: ['STOCK_READER', 'SUPPORT_OPERATOR'],
}

export const CAMERA_MAINTENANCE_LEGACY_RIGHTS: LegacyRightsRequirement = {
  any: ['STOCK_ADMIN', 'STOCK_READER', 'SUPPORT_OPERATOR'],
}

export const CAMERA_CONFIG_LEGACY_RIGHTS = CAMERA_OPERATOR_LEGACY_RIGHTS
